/**
 * WHICH VERSION a new owner gets — of the sign-up / log-in page AND of the onboarding:
 *   classic     Login.vue + CreateBusiness.vue (unchanged)
 *   playground  LoginPlayground.vue + CreateBusinessPlayground.vue (the live 3D floor)
 * Owner, 2026-10-22: "keep in code the old one, open this as a new page so we can easily shift
 * back", and A/B test the two.
 *
 * ⭐ THE SWITCH IS `ONBOARDING_MODE`, below:
 *   'ab'          each device / account is assigned once, 50/50, and keeps its version
 *   'playground'  everyone gets the playground
 *   'classic'     everyone gets the classic pages — the one-line way back
 *
 * Two places remember the version, because the log-in page has no user yet:
 *   - the DEVICE (localStorage `dd_onboarding_variant`) — for the log-in / sign-up page;
 *   - the ACCOUNT (`_User.onboarding_variant`) — for the onboarding, so a returning owner always
 *     sees the same one, and the funnel report can split onboarding_step by version.
 * A new account inherits the device's version, so whoever signed up on the new page also gets
 * the new onboarding. `?flow=classic` / `?flow=playground` overrides both for testing (not saved).
 */
import { Parse } from 'src/boot/parse'

export type OnboardingVariant = 'classic' | 'playground'
// export const ONBOARDING_MODE: 'ab' | OnboardingVariant = 'ab'
export const ONBOARDING_MODE: 'ab' | OnboardingVariant = 'playground'


const DEVICE_KEY = 'dd_onboarding_variant'
const isVariant = (v: unknown): v is OnboardingVariant => v === 'classic' || v === 'playground'

/** This device's version, drawn once. */
export function deviceVariant(override?: unknown): OnboardingVariant {
  if (isVariant(override)) return override
  if (ONBOARDING_MODE !== 'ab') return ONBOARDING_MODE
  try {
    const saved = localStorage.getItem(DEVICE_KEY)
    if (isVariant(saved)) return saved
    const arm: OnboardingVariant = Math.random() < 0.5 ? 'classic' : 'playground'
    localStorage.setItem(DEVICE_KEY, arm)
    return arm
  } catch {
    // Storage blocked: decide per visit, which is still a fair split.
    return Math.random() < 0.5 ? 'classic' : 'playground'
  }
}

/** The signed-in account's version for the onboarding: its own, else the device's (then saved). */
export function onboardingVariant(override?: unknown): OnboardingVariant {
  if (isVariant(override)) return override
  if (ONBOARDING_MODE !== 'ab') return ONBOARDING_MODE
  const user = Parse.User.current()
  const saved = user?.get('onboarding_variant')
  if (isVariant(saved)) return saved
  const arm = deviceVariant()
  if (user) {
    user.set('onboarding_variant', arm)
    // Best effort: an unsaved version only means the owner may be re-drawn on a later visit.
    user.save().catch(() => {})
  }
  return arm
}
